import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { SimpleAlert } from "../components/Alerts/Alerts";
import BackDropLoader from "../components/Loaders/BackDropLoader";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import {
  startGetTokenCanva,
  startGetTokenGoogleAds,
  startGetTokenGoogleDrive,
  startGetTokenLinkedInAds,
  startGetTokenTiktok,
  startGetTokenTiktokBusiness,
  startGetTokenYouTube,
  startResolveOAuthPlatform,
} from "../actions/user";
import {
  OK,
  OAUTH_CALLBACK_ACCOUNT_CONNECTED_TEXT,
  OAUTH_CALLBACK_ACCOUNT_CONNECTED_TITLE,
  OAUTH_IN_WINDOW_NO_COMPLETED,
} from "../i18n/keysTranslations";
import {
  ALERT_ICON_TYPE_SUCCESS,
  CANVA,
  GOOGLE,
  LINKEDIN,
  MODE_BACK_DROP_CONNECT,
  TIKTOK,
  TIKTOK_BUSINESS,
  YOUTUBE,
} from "../utils/constants";

const GOOGLE_DRIVE = "googleDrive";

const SETTINGS_PATH = "/settings";
const SETTINGS_AD_ACCOUNTS_TAB = "accounts";

const OAUTH_CALLBACK_SKIP_PATH_PREFIXES = [
  "/appSumo",
  "/pre-activation",
  "/enterprise-intake",
  "/campaign-approval",
  "/content-approval",
];

const OAUTH_CALLBACK_QUERY_KEYS = [
  "code",
  "auth_code",
  "state",
  "error",
  "scope",
];

const isOAuthCallbackSkipPath = (pathname) =>
  OAUTH_CALLBACK_SKIP_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );

const getOAuthCallbackPayload = (search) => {
  const params = new URLSearchParams(search);
  const state = params.get("state");
  const code = params.get("code");
  const authCode = params.get("auth_code");
  const error = params.get("error");

  if (!state) {
    return null;
  }

  if (error && !code && !authCode) {
    return {
      state,
      code: null,
      authCode: null,
      error,
    };
  }

  if (!code && !authCode) {
    return null;
  }

  return {
    state,
    code,
    authCode,
    error,
  };
};

const stripOAuthCallbackParams = (search) => {
  const params = new URLSearchParams(search);

  OAUTH_CALLBACK_QUERY_KEYS.forEach((key) => {
    params.delete(key);
  });

  const next = params.toString();
  return next ? `?${next}` : "";
};

const buildSettingsAdAccountsSearch = (search) => {
  const params = new URLSearchParams(search);
  params.set("tab", SETTINGS_AD_ACCOUNTS_TAB);
  return params.toString();
};

const getOAuthCallbackHandledKey = (state) => `oauthCallbackHandled:${state}`;

const getOAuthCallbackInFlightKey = (state) => `oauthCallbackInFlight:${state}`;

const isOAuthCallbackHandled = (state) =>
  typeof window !== "undefined" &&
  sessionStorage.getItem(getOAuthCallbackHandledKey(state)) === "1";

const isOAuthCallbackInFlight = (state) =>
  typeof window !== "undefined" &&
  sessionStorage.getItem(getOAuthCallbackInFlightKey(state)) === "1";

const markOAuthCallbackHandled = (state) => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(getOAuthCallbackHandledKey(state), "1");
  sessionStorage.removeItem(getOAuthCallbackInFlightKey(state));
};

const markOAuthCallbackInFlight = (state) => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(getOAuthCallbackInFlightKey(state), "1");
};

const clearOAuthCallbackInFlight = (state) => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(getOAuthCallbackInFlightKey(state));
};

const platformOAuthRegistry = {
  [GOOGLE]: {
    platform: GOOGLE,
    exchangeToken: startGetTokenGoogleAds,
  },
  [YOUTUBE]: {
    platform: YOUTUBE,
    exchangeToken: startGetTokenYouTube,
  },
  [LINKEDIN]: {
    platform: LINKEDIN,
    exchangeToken: startGetTokenLinkedInAds,
  },
  [TIKTOK]: {
    platform: TIKTOK,
    exchangeToken: startGetTokenTiktok,
  },
  [TIKTOK_BUSINESS]: {
    platform: TIKTOK_BUSINESS,
    exchangeToken: startGetTokenTiktokBusiness,
  },
  [CANVA]: {
    platform: CANVA,
    exchangeToken: startGetTokenCanva,
    normalizeResult: (result) => ({ ok: Boolean(result) }),
  },
  [GOOGLE_DRIVE]: {
    platform: GOOGLE_DRIVE,
    exchangeToken: startGetTokenGoogleDrive,
  },
};

const getPlatformOAuthHandler = (platform) =>
  platformOAuthRegistry[platform] || null;

const buildOAuthTokenPayload = (handler, { code, authCode, state }) => {
  if (!state) {
    return null;
  }

  switch (handler.platform) {
    case TIKTOK: {
      const value = code || authCode;
      if (!value) return null;
      return { code: value, state, withAuth: true };
    }
    case TIKTOK_BUSINESS: {
      const value = authCode || code;
      if (!value) return null;
      return { authCode: value, state, withAuth: true };
    }
    case CANVA: {
      const value = code || authCode;
      if (!value) return null;
      return { code: value, state, withAuth: true };
    }
    default: {
      const value = code || authCode;
      if (!value) return null;
      return { authCode: value, state, withAuth: true };
    }
  }
};

export const usePlatformOAuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { user, isLoading: isLoadingAuth } = useSelectorApp(
    (state) => state.auth
  );

  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [backdropPlatform, setBackdropPlatform] = useState(GOOGLE);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (isLoadingAuth || !user?.uid) {
      return;
    }

    if (isOAuthCallbackSkipPath(location.pathname)) {
      return;
    }

    const callbackPayload = getOAuthCallbackPayload(location.search);
    if (!callbackPayload) {
      return;
    }

    const { state: oauthState, code, authCode, error } = callbackPayload;

    const stripCallbackParams = (pathname = window.location.pathname) => {
      const currentSearch = window.location.search;
      const nextSearch = stripOAuthCallbackParams(currentSearch);

      if (nextSearch !== currentSearch) {
        navigate({ pathname, search: nextSearch }, { replace: true });
      }
    };

    if (isOAuthCallbackHandled(oauthState)) {
      stripCallbackParams(
        window.location.pathname === SETTINGS_PATH
          ? SETTINGS_PATH
          : window.location.pathname
      );
      return;
    }

    if (isOAuthCallbackInFlight(oauthState)) {
      return;
    }

    const handleOAuthDenied = async () => {
      clearOAuthCallbackInFlight(oauthState);
      stripCallbackParams();

      await SimpleAlert({
        title: t(OAUTH_IN_WINDOW_NO_COMPLETED),
        icon: "warning",
        confirmButtonText: t(OK),
      });
    };

    const handleOAuthCallback = async () => {
      if (isProcessingRef.current) {
        return;
      }

      isProcessingRef.current = true;
      markOAuthCallbackInFlight(oauthState);
      setBackdropPlatform(GOOGLE);
      setOpenBackDrop(true);

      if (error) {
        isProcessingRef.current = false;
        setOpenBackDrop(false);
        await handleOAuthDenied();
        return;
      }

      const settingsSearch = buildSettingsAdAccountsSearch(location.search);
      const settingsUrlSearch = settingsSearch ? `?${settingsSearch}` : "";

      if (
        location.pathname !== SETTINGS_PATH ||
        location.search !== settingsUrlSearch
      ) {
        navigate(
          {
            pathname: SETTINGS_PATH,
            search: settingsUrlSearch,
          },
          { replace: true }
        );
      }

      try {
        const resolveResult = await dispatch(
          startResolveOAuthPlatform({ state: oauthState })
        );

        if (!resolveResult?.ok || !resolveResult?.platform) {
          return;
        }

        const handler = getPlatformOAuthHandler(resolveResult.platform);
        if (!handler) {
          return;
        }

        setBackdropPlatform(handler.platform);

        const tokenPayload = buildOAuthTokenPayload(handler, {
          code,
          authCode,
          state: oauthState,
        });

        if (!tokenPayload) {
          return;
        }

        const exchangeResult = await dispatch(
          handler.exchangeToken(tokenPayload)
        );
        const normalizedResult = handler.normalizeResult
          ? handler.normalizeResult(exchangeResult)
          : exchangeResult;

        if (!normalizedResult?.ok) {
          return;
        }

        markOAuthCallbackHandled(oauthState);

        await SimpleAlert({
          title: t(OAUTH_CALLBACK_ACCOUNT_CONNECTED_TITLE),
          text: t(OAUTH_CALLBACK_ACCOUNT_CONNECTED_TEXT),
          icon: ALERT_ICON_TYPE_SUCCESS,
          confirmButtonText: t(OK),
        });
      } finally {
        clearOAuthCallbackInFlight(oauthState);
        setOpenBackDrop(false);
        isProcessingRef.current = false;
        stripCallbackParams(SETTINGS_PATH);
      }
    };

    handleOAuthCallback();
  }, [
    dispatch,
    isLoadingAuth,
    location.pathname,
    location.search,
    navigate,
    t,
    user?.uid,
  ]);

  if (!openBackDrop) {
    return null;
  }

  return createPortal(
    <BackDropLoader
      open={openBackDrop}
      platform={backdropPlatform}
      from={MODE_BACK_DROP_CONNECT.GET_INFORMATION}
    />,
    document.body
  );
};
